import { Link } from 'react-router-dom';
import type { ProductSummary } from '@chicare/shared';

import { productPath } from '@/lib/sector';
import { Card } from '@/components/ui/Card';
import { PackBox } from '@/components/ui/PackBox';
import { Badge } from '@/components/ui/Badge';
import { Icon } from '@/components/icons/Icon';

/**
 * ProductCard — a product in a grid.
 *
 * The three numbers under the name are the whole ChiCare proposition compressed
 * into one line: the lowest price, how many pharmacies are competing for it,
 * and how many packs of it have been verified. A card that showed only a price
 * would be an ordinary e-commerce tile.
 *
 * `expiring_soon_count` renders amber and only amber (§11) — it is the "stock
 * running out" signal, never a promotion.
 *
 * ── THE ANIMAL MARKER TRAVELS WITH THE CARD ─────────────────────────────────
 *
 * A card is not only seen on its own shelf. It is screenshotted, shared, and
 * rendered in a cart line and an order history row, and in all of those the
 * violet chrome around it is gone. So the card carries its own marker: the
 * species it is for, or "For animals only" where the catalogue has not recorded
 * one. The chrome makes the shelf obvious; this makes the CARD unambiguous
 * wherever it ends up.
 */

/**
 * The species a veterinary product is for, or a plain statement where the
 * catalogue does not know yet.
 *
 * Two species are named and the rest counted, because "Cattle · Goats · Sheep ·
 * Pigs" on a 12px line at 393px wraps to three lines and pushes the price off
 * the card.
 */
export function AnimalMarker({ product }: { product: ProductSummary }) {
  if (!product.is_veterinary) {
    return null;
  }

  const species = product.species ?? [];
  const shown = species.slice(0, 2).map((one) => one.label);
  const rest = species.length - shown.length;

  return (
    <span
      data-testid="animal-marker"
      title={species.map((one) => one.label).join(', ')}
      className="inline-flex max-w-full items-center gap-1 rounded-pill bg-blue-tint/70 px-1.5 py-[2px] text-[10.5px] font-semibold leading-[1.45] text-blue"
    >
      <Icon name="paw" size={10} strokeWidth={2} className="shrink-0" />
      <span className="truncate">
        {species.length === 0
          ? 'Animals only'
          : `${shown.join(', ')}${rest > 0 ? ` +${rest}` : ''}`}
      </span>
    </span>
  );
}

export interface ProductCardProps {
  product: ProductSummary;
  className?: string;
}

export function ProductCard({ product, className }: ProductCardProps) {
  return (
    <Card className={className} flush>
      <Link to={productPath(product)} className="block p-3 no-underline">
        <PackBox
          pack={product.pack_art}
          src={product.image}
          alt={product.display_name}
          className="mb-2.5 h-[150px] sm:h-[170px]"
        />

          {/* ────────── A PAID PLACEMENT HAS TO SAY SO ──────────
              Position in a list is the only thing money buys here: the price
              under this is still the lowest rung of the same offer ladder an
              unfeatured product gets. But a buyer has no way of knowing a
              seller paid for the position, so the card tells them — the same
              contract the /sponsored block has, for the same reason.

              Quiet on purpose. It is a disclosure, not a sales badge, and one
              that shouted would be doing the advertiser's work. */}
          {product.is_featured ? (
            <span
              data-testid="featured-mark"
              className="mb-1 inline-flex items-center rounded-[5px] bg-paper px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.07em] text-faint"
            >
              Featured
            </span>
          ) : null}

        {product.is_veterinary ? (
          <span className="mb-1 block">
            <AnimalMarker product={product} />
          </span>
        ) : null}

        <b className="block text-[12.5px] leading-[1.35] text-ink">
          {product.display_name}
          {product.pack_size ? <span className="font-normal"> · {product.pack_size}</span> : null}
        </b>

        {product.manufacturer ? (
          <div className="mt-[3px] text-[11px] text-faint">{product.manufacturer}</div>
        ) : null}

        <div className="mt-2 flex items-baseline gap-[7px]">
          <span className="font-display text-base font-bold tracking-[-0.02em] text-blue tabular">
            {product.from_price_display ?? '—'}
          </span>
          {product.offer_count > 1 ? (
            <span className="text-[11px] text-faint">of {product.offer_count}</span>
          ) : null}
        </div>

        <div className="mt-2 flex flex-wrap gap-1.5">
          {product.verified_count > 0 ? (
            <Badge tone="verified">{product.verified_count} verified</Badge>
          ) : null}
          {product.expiring_soon_count > 0 ? (
            <Badge tone="caution">{product.expiring_soon_count} expire soon</Badge>
          ) : null}
        </div>
      </Link>
    </Card>
  );
}

/** The horizontal variant used in search results. */
export function ProductRow({ product }: { product: ProductSummary }) {
  return (
    <Card flush>
      <Link to={productPath(product)} className="flex gap-3 p-3 no-underline">
        <PackBox
          pack={product.pack_art}
          src={product.image}
          alt={product.display_name}
          className="h-[104px] w-[104px] shrink-0 sm:h-[116px] sm:w-[116px]"
        />

        <div className="min-w-0 flex-1">
          {/* Same disclosure as the grid card. In search results the row is
              NOT moved up the list for being featured — that list is ordered
              cheapest first and says so — so here the label is the whole of
              what was paid for. */}
          {product.is_featured ? (
            <span
              data-testid="featured-mark"
              className="mb-1 inline-flex items-center rounded-[5px] bg-paper px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.07em] text-faint"
            >
              Featured
            </span>
          ) : null}

          {product.is_veterinary ? (
            <span className="mb-1 block">
              <AnimalMarker product={product} />
            </span>
          ) : null}

          <b className="block text-[13.5px] leading-[1.3] text-ink">{product.display_name}</b>
          <p className="mt-[3px] text-[11px] text-faint">
            {[
              product.manufacturer,
              product.pack_size,
              // "Prescription" means a VET's prescription on this side of the
              // wall, and a pharmacist cannot write one.
              product.is_pom
                ? product.is_veterinary
                  ? 'Vet prescription'
                  : 'Prescription'
                : 'OTC',
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>

          <div className="mt-[7px] flex items-baseline gap-2">
            <span className="font-display text-[19px] font-bold tracking-[-0.02em] text-blue tabular">
              {product.from_price_display ?? '—'}
            </span>
            {product.offer_count > 1 ? (
              <span className="text-[11px] text-faint">lowest of {product.offer_count}</span>
            ) : null}
          </div>

          <div className="mt-[7px] flex flex-wrap gap-1.5">
            {product.verified_count > 0 ? (
              <Badge tone="verified">{product.verified_count} verified</Badge>
            ) : null}
            <Badge>
              {product.offer_count} {product.is_veterinary ? 'sellers' : 'pharmacies'}
            </Badge>
            {product.expiring_soon_count > 0 ? (
              <Badge tone="caution">{product.expiring_soon_count} expire soon</Badge>
            ) : null}
          </div>
        </div>
      </Link>
    </Card>
  );
}
